import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import PaginationArrowLeftIcon from '../../components/icons/PaginationArrowLeftIcon';
import PaginationArrowRightIcon from '../../components/icons/PaginationArrowRightIcon';
import AddIcon from '../../components/icons/AddIcon';
import SearchIcon from '../../components/icons/SearchIcon'; // Import SearchIcon
import EditIcon from '../../components/icons/EditIcon';
import DeleteIcon from '../../components/icons/DeleteIcon';
import {
    draggable,
    dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

// Define roleOptions outside the component for a stable reference
const roleOptions = [ // Same as in FAQManagerFormAdminScreen for consistency
    { value: 'user', label: 'Cliente' },
    { value: 'agent', label: 'Asesor' },
    { value: 'supplier', label: 'Proveedor' },
    { value: 'admin', label: 'Administrador' },
];

const FAQManagerListAdminScreen = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const ITEMS_PER_PAGE = 5; // Number of items per page for pagination

    const { token } = useAuth();
    const [faqs, setFaqs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Removed activeTab state
    const [filterRole, setFilterRole] = useState('');
    const [searchTerm, setSearchTerm] = useState(''); // Added searchTerm state
    const [currentPage, setCurrentPage] = useState(1); // Current page for the single table
    const [dndState, setDndState] = useState({
        isDragging: false,      // Is any item currently being dragged?
        draggedItemId: null,    // ID of the item being dragged
        dropTargetFaqId: null,  // ID of the FAQ row the cursor is over (for drop indication)
        dropEdge: null,         // 'top' or 'bottom' relative to dropTargetFaqId
    });
    const tbodyRef = useRef(null); // Single tbody ref for the main table

    // Memoize fetchFaqs
    const fetchFaqs = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/faqs`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Error fetching FAQs: ${response.statusText}` }));
                throw new Error(errorData.message || `Error fetching FAQs: ${response.statusText}`);
            }
            const data = await response.json();
            setFaqs(data);
        } catch (err) {
            setError(err.message);
            console.error("Error fetching FAQs:", err);
            setFaqs([]); // Clear faqs on error
        } finally {
            setIsLoading(false);
        }
    }, [API_BASE_URL, token]);

    useEffect(() => {
        if (token) {
            fetchFaqs();
        }
    }, [token, fetchFaqs]); // No longer depends on filterRole for fetching, fetches all

    // Effect to reset currentPage when filterRole changes
    useEffect(() => {
        setCurrentPage(1); // Reset page on filter or search change
    }, [filterRole, searchTerm]); // Added searchTerm to dependencies

    // Memoize handleSaveOrder
    const handleSaveOrder = useCallback(async (faqsToOrderForRole) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/faqs/order`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ faqsToOrder: faqsToOrderForRole }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Error al actualizar el orden de las P+F.' }));
                throw new Error(errorData.message || 'Error al actualizar el orden de las P+F.');
            }

            await fetchFaqs(); // Refetch FAQs to reflect the new order
        } catch (err) {
            setError(err.message);
            console.error("Error updating FAQ order:", err);
        } finally {
            setIsLoading(false);
        }
    }, [API_BASE_URL, token, fetchFaqs]);

    const handleDeleteFAQ = async (faqId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar esta P+F?')) {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/faqs/${faqId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: 'Error al eliminar P+F.' }));
                    throw new Error(errorData.message || 'Error al eliminar P+F.');
                }
                await fetchFaqs(); // Refetch FAQs to update the list and pagination
            } catch (err) {
                console.error("Error deleting FAQ:", err);
                setError(`Error al eliminar P+F: ${err.message}`);
                // alert(`Error al eliminar P+F: ${err.message}`); // Using setError state instead
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Filter and sort FAQs based on filterRole and display_order
    const searchableColumns = ['question', 'answer']; // Define columns to search
    const filteredAndSortedFaqs = faqs
        .filter(faq => {
            // 1. Filter by search term across specified columns
            const matchesSearchTerm = searchTerm.trim() === '' ||
                searchableColumns.some(column => {
                    const value = String(faq[column]).toLowerCase();
                    return value.includes(searchTerm.toLowerCase());
                });
            return (filterRole === '' || faq.role === filterRole) && matchesSearchTerm; // Combine role filter and search filter
        })
        .sort((a, b) => a.display_order - b.display_order);

    const totalItems = filteredAndSortedFaqs.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    // Calculate the start and end index for the current page
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedFaqs = filteredAndSortedFaqs.slice(startIndex, endIndex);

    const handlePageChange = (newPage) => {
        const validNewPage = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(validNewPage);
    };

    // Determine if DND should be active (only when a specific role is selected)
    const isDndActive = filterRole !== '';

    return (
        <>
            <FormSpinnerOverlay isLoading={isLoading} />
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-0">Gestionar Preguntas Frecuentes</h1>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Link to="/settings/faqs/add" className="btn btn-success d-flex align-items-center">
                        <AddIcon />
                        Añadir FAQ
                    </Link>
                </div>
            </div>

            <div className="row g-3 mb-3 align-items-center d-flex justify-content-between">
                <div className="col-md-7 col-lg-8">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar FAQs..."
                            aria-label="Buscar FAQs"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="btn btn-outline-secondary" type="button">
                            <SearchIcon />
                        </button>
                    </div>                </div>
                <div className="col-md-5 col-lg-4">
                    <select
                        className="form-select"
                        id="filtroRolUsuario"
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                    >
                        {roleOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>
            {error && <AlertMessage type="danger" message={`${error}`} />} {/* Simplified error message display */}

            {!isLoading && !error && faqs.length === 0 && (
                <AlertMessage type="info" message="No hay preguntas frecuentes registradas en el sistema." />
            )}

            {!isLoading && faqs.length > 0 && ( // Removed !error here, error message will show above if present
                <>
                    {/* Removed nav-tabs and tab-content */}
                    <FAQTableDndSetup
                        isActive={isDndActive} // DND is active only when a specific role is filtered
                        faqsInTab={filteredAndSortedFaqs} // All filtered FAQs for DND calculations
                        paginatedFaqsInTab={paginatedFaqs} // Only paginated for rendering
                        tbodyRef={tbodyRef}
                        roleValue={filterRole} // Pass the selected filter role
                        setDndState={setDndState}
                        dndState={dndState}
                        onOrderSave={handleSaveOrder}
                        setFaqs={setFaqs}
                    />
                    {totalItems === 0 ? (
                        <div className="mt-3">
                            <AlertMessage type="info" message={`No hay preguntas frecuentes registradas para el rol: ${roleOptions.find(opt => opt.value === filterRole)?.label || 'seleccionado'}.`} />
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive mt-3">
                                <table className="table table-striped table-sm faq-admin-table">
                                    <thead>
                                        <tr style={
                                            {
                                                verticalAlign: 'middle',
                                                height: '45px',
                                                margin: '5px'
                                            }
                                        }>
                                            <th className="question-column">Pregunta</th>
                                            <th className="answer-column">Respuesta (Extracto)</th>
                                            <th className="actions-column">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody ref={tbodyRef}>
                                        {paginatedFaqs.map((faq) => {
                                            const isBeingDragged = dndState.draggedItemId === faq.id;
                                            let rowClassName = "faq-table-row";
                                            if (isBeingDragged) {
                                                rowClassName += " dragging";
                                            }
                                            // Apply drop indicator only if DND is active for this role
                                            if (isDndActive && dndState.dropTargetFaqId === faq.id && dndState.dropEdge) {
                                                rowClassName += ` drop-indicator-${dndState.dropEdge}`;
                                            }

                                            return (
                                                <tr
                                                    key={faq.id}
                                                    data-faq-id={faq.id}
                                                    className={rowClassName}
                                                    style={{ opacity: isBeingDragged ? 0.5 : 1 }}
                                                >
                                                    <td style={{ verticalAlign: 'middle' }}>{faq.question}</td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        {faq.answer.substring(0, 100)}{faq.answer.length > 100 ? '...' : ''}
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'right' }}>
                                                        <Link to={`/settings/faqs/edit/${faq.id}`} className="btn btn-sm btn-outline-checkin me-2" title="Editar">
                                                            <EditIcon />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDeleteFAQ(faq.id)}
                                                            className="btn btn-sm btn-outline-danger"
                                                            title="Eliminar"
                                                        >
                                                            <DeleteIcon />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <nav aria-label={`Paginación de Preguntas Frecuentes`} className="mt-3">
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} aria-label="Anterior">
                                                <PaginationArrowLeftIcon />
                                            </button>
                                        </li>
                                        {[...Array(totalPages).keys()].map(page => (
                                            <li key={page + 1} className={`page-item ${currentPage === page + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(page + 1)}>
                                                    {page + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} aria-label="Siguiente">
                                                <PaginationArrowRightIcon />
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

const FAQTableDndSetup = ({
    isActive,
    faqsInTab,
    paginatedFaqsInTab,
    tbodyRef,
    roleValue,
    setDndState,
    dndState,
    onOrderSave,
    setFaqs,
}) => {
    const itemRefs = useRef(new Map());

    useEffect(() => {
        itemRefs.current = new Map();
        if (tbodyRef.current) { // Ensure tbodyRef.current exists
            paginatedFaqsInTab.forEach(faq => {
                const el = tbodyRef.current.querySelector(`tr[data-faq-id='${faq.id}']`);
                if (el) itemRefs.current.set(faq.id, el);
            });
        }
    }, [paginatedFaqsInTab, tbodyRef]); // Added tbodyRef to dependencies

    useEffect(() => {
        if (!isActive || !tbodyRef.current || roleValue === '') return () => { }; // DND is only active if a specific role is selected

        const currentTbody = tbodyRef.current;
        const subscriptions = [];

        paginatedFaqsInTab.forEach((faq) => {
            const el = itemRefs.current.get(faq.id);
            if (!el) return;

            subscriptions.push(
                draggable({
                    element: el,
                    getInitialData: () => ({ id: faq.id, role: faq.role, type: 'faq-item' }), // Use faq.role directly
                    onDragStart: ({ source }) => setDndState(s => ({ ...s, isDragging: true, draggedItemId: source.data.id })),
                    onDrop: () => setDndState(s => ({ ...s, isDragging: false, draggedItemId: null, dropTargetFaqId: null, dropEdge: null })),
                })
            );
        });

        subscriptions.push(
            dropTargetForElements({
                element: currentTbody,
                canDrop: ({ source }) => source.data.type === 'faq-item' && source.data.role === roleValue, // Only allow dropping if the dragged item's role matches the currently filtered role
                onDrag: ({ location, source }) => {
                    if (source.data.type !== 'faq-item' || source.data.role !== roleValue) return;

                    const { clientY } = location.current.input;
                    let newDropTargetId = null;
                    let newEdge = null;

                    for (const paginatedFaq of paginatedFaqsInTab) {
                        const rowEl = itemRefs.current.get(paginatedFaq.id);
                        if (rowEl) {
                            const rect = rowEl.getBoundingClientRect();
                            if (clientY >= rect.top && clientY <= rect.bottom) {
                                newDropTargetId = paginatedFaq.id;
                                newEdge = (clientY < rect.top + rect.height / 2) ? 'top' : 'bottom';
                                break;
                            }
                        }
                    }

                    if (newDropTargetId === null && paginatedFaqsInTab.length > 0) {
                        const firstRowEl = itemRefs.current.get(paginatedFaqsInTab[0].id);
                        const lastRowEl = itemRefs.current.get(paginatedFaqsInTab[paginatedFaqsInTab.length - 1].id);

                        if (firstRowEl) {
                            const firstRowRect = firstRowEl.getBoundingClientRect();
                            if (clientY < firstRowRect.top) {
                                newDropTargetId = paginatedFaqsInTab[0].id;
                                newEdge = 'top';
                            }
                        }
                        if (lastRowEl && newDropTargetId === null) { // Check if not already set
                            const lastRowRect = lastRowEl.getBoundingClientRect();
                            if (clientY > lastRowRect.bottom) {
                                newDropTargetId = paginatedFaqsInTab[paginatedFaqsInTab.length - 1].id;
                                newEdge = 'bottom';
                            }
                        }
                    }
                    setDndState(s => ({ ...s, dropTargetFaqId: newDropTargetId, dropEdge: newEdge }));
                },
                onDragLeave: () => {
                    setDndState(s => ({ ...s, dropTargetFaqId: null, dropEdge: null }));
                },
                onDrop: ({ source }) => {
                    const sourceId = source.data.id;
                    const { dropTargetFaqId: currentDropTargetId, dropEdge: currentDropEdge } = dndState;

                    setDndState(s => ({ ...s, isDragging: false, draggedItemId: null, dropTargetFaqId: null, dropEdge: null }));

                    let reorderedItemsForRole = [...faqsInTab]; // Use faqsInTab (which is filteredAndSortedFaqs) for reordering
                    const sourceItemIndex = reorderedItemsForRole.findIndex(f => f.id === sourceId);
                    if (sourceItemIndex === -1) return;

                    const [itemToMove] = reorderedItemsForRole.splice(sourceItemIndex, 1);
                    let targetIndex = -1;

                    if (currentDropTargetId && currentDropTargetId !== sourceId) {
                        targetIndex = reorderedItemsForRole.findIndex(f => f.id === currentDropTargetId);
                        if (targetIndex !== -1) {
                            if (currentDropEdge === 'top') {
                                reorderedItemsForRole.splice(targetIndex, 0, itemToMove);
                            } else {
                                reorderedItemsForRole.splice(targetIndex + 1, 0, itemToMove);
                            }
                        } else { // Target not found in current paginated view, append to end of all filtered items
                            reorderedItemsForRole.push(itemToMove);
                        }
                    } else if (currentDropTargetId === sourceId) { // Dropped on itself, put back
                        reorderedItemsForRole.splice(sourceItemIndex, 0, itemToMove);
                    } else { // No specific target item (e.g., empty space or only one item)
                        // If dragging to the very top of an empty or non-empty list (and edge was 'top' for first item)
                        if (currentDropTargetId && currentDropEdge === 'top' && reorderedItemsForRole.findIndex(f => f.id === currentDropTargetId) === 0) {
                            reorderedItemsForRole.unshift(itemToMove);
                        } else { // Default to end
                            reorderedItemsForRole.push(itemToMove);
                        }
                    }

                    // Update the main faqs state with the new order for the specific role
                    setFaqs(prevFaqs => {
                        // Filter out FAQs of the current role from the previous state
                        const otherRoleFaqs = prevFaqs.filter(f => f.role !== roleValue);
                        // Map the reordered items for the current role with new display_order
                        const updatedRoleFaqsWithNewOrder = reorderedItemsForRole.map((faq, index) => ({
                            ...faq,
                            display_order: index,
                        }));
                        // Combine and sort globally (by role then display_order)
                        return [...otherRoleFaqs, ...updatedRoleFaqsWithNewOrder].sort((a, b) => {
                            // Sort by role first, then by display_order within the same role
                            const roleAIndex = roleOptions.findIndex(r => r.value === a.role);
                            const roleBIndex = roleOptions.findIndex(r => r.value === b.role);
                            if (roleAIndex !== roleBIndex) {
                                return roleAIndex - roleBIndex;
                            }
                            return a.display_order - b.display_order;
                        });
                    });

                    const payload = reorderedItemsForRole.map((faq, index) => ({
                        id: faq.id,
                        display_order: index,
                    }));
                    onOrderSave(payload);
                },
            })
        );

        return () => subscriptions.forEach(unsubscribe => unsubscribe()); // Cleanup function
    }, [isActive, faqsInTab, paginatedFaqsInTab, roleValue, setDndState, dndState, onOrderSave, setFaqs, tbodyRef, roleOptions]); // Added roleOptions to dependencies

    return null;
};

export default FAQManagerListAdminScreen;
